













































































































































































import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import store from "@/store";
import moment from "moment";
//  @types
import { Header, Row } from "@/types/VDataTable";
import { VesselAccessGroup } from "@/types/vesselAccessGroup";
//  @components
import { headers } from "@/components/Settings/VesselAccessControl/headers";
import SortableArrows from "@/components/SortableArrows.vue";
import DataCell from "./DataCell.vue";
import Tooltip from "@/components/Tooltip.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import VesselAccessGroupDataModal from "@/components/Settings/VesselAccessControl/VesselAccessGroupDataModal.vue";

// @clients
import VesselAccessGroupsClient from "Clients/vessel-categories-client";

//  @modules
import { getModule } from "vuex-module-decorators";
import VesselAccessGroupsModule from "@/store/clients/VesselAccessGroups.module";
import UsersModule from "@/store/clients/Users.module";
import SnackbarModule from "@/store/clients/Snackbar.module";

const VesselAccessGroups = getModule(VesselAccessGroupsModule, store);
const Users = getModule(UsersModule, store);
const Snackbar = getModule(SnackbarModule, store);

@Component({
  components: {
    SortableArrows,
    DataCell,
    Tooltip,
    ConfirmDialog,
    VesselAccessGroupDataModal,
  },
})
export default class DataTable extends Vue {
  @Ref("confirm") confirm!: any;
  // @Data
  loading = false;
  isLoadingTextDisplay = false;
  sortDesc = false;
  searchQuery = "";
  headers: Header[] = headers;
  sortBy = "";
  pageLimit = -1; // -1 show all items by default
  pageCount = 0;
  currentItems: Row[] = [];
  currentPage = 1;
  selectedVesselAccessGroup: VesselAccessGroup | null = null;
  VesselAccessGroupDataModal = false;

  @Watch("VesselAccessGroupDataModal")
  onVesselAccessGroupDataModalClose(newVal: boolean) {
    if (!newVal) this.selectedVesselAccessGroup = null;
  }

  // @Getters
  get isLessThanLimit(): boolean {
    return this.rows.length <= this.pageLimit;
  }

  get skeletonStructure(): object {
    return { "table-row": `table-cell@4` };
  }

  get filteredHeaders(): Header[] {
    const result = this.headers.filter(header => {
      return true;
    });
    return result;
  }

  get rows(): any {
    let rows = this.vesselAccessGroups.map((g, index) => {
      return {
        index: index,
        vesselAccessGroupId: g.id,
        id: {
          value: g.id === -1 ? "" : g.id,
        },
        name: {
          value: g.name,
        },
        vesselCount: {
          value: g.vessels.length,
        },
        userCount: {
          value: g.users.length,
        },
        actions: {
          value: ["edit"],
        },
      };
    });

    // Sort groups by name alphabetically
    rows = rows.sort((a, b) => {
      const nameA = (a.name.value || "").toLowerCase();
      const nameB = (b.name.value || "").toLowerCase();

      return nameA.localeCompare(nameB);
    });

    // Filter by search query
    if (this.searchQuery && this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      rows = rows.filter(item => {
        // Search by user name or email number
        const nameMatch = item.name.value ? item.name.value.toLowerCase().includes(query) : false;
        return nameMatch;
      });
    }

    return rows;
  }

  get sortedRows() {
    if (!this.sortBy) return this.rows;

    return this.rows.slice().sort((a: any, b: any) => {
      // Default string/other sorting
      const valueA = a[this.sortBy]?.value ?? "";
      const valueB = b[this.sortBy]?.value ?? "";

      // Normalize values for comparison
      const normalizedA = typeof valueA === "string" ? valueA.toLowerCase() : valueA;
      const normalizedB = typeof valueB === "string" ? valueB.toLowerCase() : valueB;

      if (this.sortDesc) {
        return normalizedA < normalizedB ? 1 : normalizedA > normalizedB ? -1 : 0;
      } else {
        return normalizedA > normalizedB ? 1 : normalizedA < normalizedB ? -1 : 0;
      }
    });
  }

  get vesselAccessGroups(): VesselAccessGroup[] {
    return VesselAccessGroups.vesselAccessGroups;
  }

  // @Methods
  cellData(row: Row, header: Header, index: number): any {
    return {
      column: header.value,
      columnName: header.text,
      vesselAccessGroupId: row.vesselAccessGroupId,
      type: header.type,
      align: header.align,
      value: row[header.value].value,
      subtitle: row[header.value].subtitle,
      link: row[header.value].link,
    };
  }

  async initializeTable(): Promise<void> {
    try {
      this.loading = true;
      this.isLoadingTextDisplay = false;
      await VesselAccessGroups.refreshVesselAccessGroups();

      // Show loading text if data takes more than 2 seconds to load
      setTimeout(() => {
        if (this.loading) {
          this.isLoadingTextDisplay = true;
        }
      }, 2000);
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to load vessel access groups data" });
    } finally {
      this.loading = false;
      this.isLoadingTextDisplay = false;
    }
  }

  async onSubmitVesselAccessGroupData(payload: { vesselAccessGroupData: VesselAccessGroup; isNewVesselAccessGroup: boolean }): Promise<void> {
    if (payload.isNewVesselAccessGroup) this.searchQuery = payload.vesselAccessGroupData.name ?? "";
    try {
      this.loading = true;
      this.isLoadingTextDisplay = true;
      await VesselAccessGroups.refreshVesselAccessGroups();
      await this.initializeTable();
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to load vessel access groups data after adding/editing" });
    } finally {
      this.loading = false;
      this.isLoadingTextDisplay = false;
    }
  }

  sort(value: string): void {
    if (this.sortBy === value) {
      this.sortDesc = !this.sortDesc; // Toggle sort direction
      if (!this.sortDesc) this.sortBy = ""; // Reset sorting if toggled off
    } else {
      this.sortBy = value;
      this.sortDesc = false; // Default to ascending order
    }
  }

  updateSortBy(value: string): void {
    if (!value) value = "";
  }

  updateSortDesc(value: boolean): void {
    if (!value) value = false;
  }

  updateItemsPerPage(value: number): void {
    if (!value) value = 20;
    this.pageLimit = value;
  }

  headerClasses(header: any): string[] {
    return [
      header.class,
      {
        sortable: header.sortable,
        "no-sort": this.sortBy !== header.value,
      },
    ];
  }

  headerStyles(header: any): any {
    return {
      width: header.width ? header.width : "auto",
      "min-width": header.width ? header.width : "auto",
    };
  }

  addVesselAccessGroup() {
    this.VesselAccessGroupDataModal = true;
    this.$emit("add-vessel-access-group");
  }

  onEditEvent(vesselAccessGroupId: number): void {
    this.selectedVesselAccessGroup = this.vesselAccessGroups.find(v => v.id === vesselAccessGroupId) || null;
    this.VesselAccessGroupDataModal = true;
    this.$emit("edit-vessel-access-group", vesselAccessGroupId);
  }

  async onDeleteEvent(vesselAccessGroupId: number): Promise<void> {
    this.$emit("delete-vessel-access-group", vesselAccessGroupId);
  }

  // @Hooks
  async created(): Promise<void> {
    await this.initializeTable();
  }
}
